import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import type { ApiResponse, CreateBrandRequest, CreateBrandResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

/**
 * Hook to create a new brand within the user's organization.
 *
 * This mutation:
 * - Creates a new brand with the provided name and optional websiteUrl
 * - Automatically switches the user's current brand to the newly created brand
 * - Invalidates relevant queries to refresh brand-related data
 *
 * Returns 402 if the user's plan doesn't allow creating additional brands.
 */
export const useCreateBrandAction = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { refreshCurrentUser } = useAuth();

  return useMutation<ApiResponse<CreateBrandResponse>, AxiosError<ApiResponse>, CreateBrandRequest>(
    {
      mutationFn: async (data: CreateBrandRequest) => {
        const response = await api.post<ApiResponse<CreateBrandResponse>>('/brands', data);
        return response.data;
      },
      onSuccess: async (response) => {
        // Refresh the current user to update the auth context with new brand
        await refreshCurrentUser();

        // Invalidate relevant queries to refetch data for the new brand context
        queryClient.invalidateQueries({ queryKey: ['organizationBrands'] });
        queryClient.invalidateQueries({ queryKey: ['currentBrand'] });
        queryClient.invalidateQueries({ queryKey: ['planFeatures'] });

        toaster.success({
          title: t('brandMenu.createSuccess'),
          description: t('brandMenu.createSuccessDescription', {
            brandName: response.data?.name,
          }),
        });
      },
      onError: (error) => {
        const status = error.response?.status;
        const errorMessage = error.response?.data?.message;

        // Handle 402 Payment Required (plan limit exceeded)
        if (status === 402) {
          toaster.error({
            title: t('brandMenu.planLimitReached'),
            description: errorMessage || t('brandMenu.planLimitReachedDescription'),
          });
          return;
        }

        // Handle 409 Conflict (brand name already exists)
        if (status === 409) {
          toaster.error({
            title: t('brandMenu.createError'),
            description: errorMessage || t('brandMenu.brandNameAlreadyExists'),
          });
          return;
        }

        // Handle other errors
        toaster.error({
          title: t('common.error'),
          description: errorMessage || t('brandMenu.createError'),
        });
      },
    }
  );
};
